import {yupResolver} from "@hookform/resolvers/yup";
import {useEffect} from "react";
import {Controller, FieldErrors, FormProvider, useForm} from "react-hook-form";
import * as Yup from "yup";

import {Divider} from "@/components/divider/Divider.tsx";
import FormErrorAlerts from "@/components/form/FormErrorAlerts.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {CredentialSecret, CredentialType} from "@/lib/types/credentials.ts";

import {CredentialFormDisplayNameDescriptionInputs} from "./CredentialFormComponents.tsx";
import {YupCredentialSecretSchema, setServerErrors} from "./CredentialFormUtils.ts";
import {CredentialTypeFormFields} from "./CredentialTypeFormFeilds.tsx";
import {CredentialTypeSelect} from "./CredentialTypeSelect/CredentialTypeSelect.tsx";

export const CreateCredentialFormSchema = Yup.object().shape({
  display_name: Yup.string().required("Required"),
  description: Yup.string().optional(),
  credential_type: Yup.mixed<CredentialType>().oneOf(Object.values(CredentialType)).required("This field is required."),
  username: Yup.string().optional(),
  secret: YupCredentialSecretSchema,
});

export interface CreateCredentialFormValues {
  display_name: string;
  description?: string;
  credential_type: CredentialType;
  username?: string;
  secret: CredentialSecret;
}

export interface CredentialCreateFormProps {
  credentialType: CredentialType;
  formId: string;
  onSubmit: (data: CreateCredentialFormValues) => void;
  onError?: (errors: FieldErrors, event?: React.BaseSyntheticEvent) => void;
  serverErrors?: ServerErrors;
}

export const CredentialCreateForm = ({
  credentialType,
  formId,
  onSubmit,
  onError,
  serverErrors,
}: CredentialCreateFormProps) => {
  const methods = useForm<CreateCredentialFormValues>({
    resolver: yupResolver<CreateCredentialFormValues>(CreateCredentialFormSchema),
    defaultValues: {
      display_name: "",
      description: "",
      credential_type: credentialType,
      secret: {
        password: undefined,
        secret: undefined,
        certificate: undefined,
        certificate_file: undefined,
        passphrase: undefined,
        key_file: undefined,
        num_digits: undefined,
        period: undefined,
      },
    },
  });

  const {
    watch,
    handleSubmit,
    setError,
    resetField,
    control,
    formState: {errors},
  } = methods;

  useEffect(() => {
    if (serverErrors) {
      setServerErrors(serverErrors, setError, "");
    }
  }, [serverErrors, setError]);

  const credentialTypeWatch = watch("credential_type");
  const resetSecretOnCredentialTypeChange = (
    currentCredentialType: CredentialType,
    newCredentialType: CredentialType
  ) => {
    if (currentCredentialType !== newCredentialType) {
      resetField("secret");
      resetField("username");
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit, onError)(e);
        }}
      >
        {serverErrors?.__all__ && <FormErrorAlerts errors={serverErrors?.__all__} />}
        <div className="form-group">
          <Controller
            control={control}
            name={"credential_type"}
            render={({field}) => (
              <CredentialTypeSelect
                value={{value: field.value, label: field.value}}
                onChange={(credentialType) => {
                  resetSecretOnCredentialTypeChange(field.value, credentialType?.value as CredentialType);
                  field.onChange(credentialType?.value);
                }}
              />
            )}
          />
          {errors.credential_type && <div className="invalid-feedback d-block">{errors.credential_type?.message}</div>}
        </div>
        <Divider className={"my-3"} />
        <CredentialFormDisplayNameDescriptionInputs />
        <Divider className={"my-3"} />
        {errors.secret && <div className="invalid-feedback d-block">{errors.secret?.message}</div>}
        <CredentialTypeFormFields credentialType={credentialTypeWatch} />
      </form>
    </FormProvider>
  );
};
